/**
 * AuthGuard - Handles authentication state and session persistence
 *
 * This component manages session state across page refreshes and multiple tabs.
 * It uses direct session management instead of useSession() hook to prevent
 * race conditions and ensure proper session restoration.
 *
 * Key Features:
 * - Direct session fetching using supabase.auth.getSession()
 * - Local state management for better control
 * - Cross-tab synchronization via storage events
 * - Proper cleanup of listeners
 *
 * Implementation Notes:
 * - Uses localStorage events to sync across tabs
 * - Handles session initialization before rendering protected routes
 * - Maintains loading state to prevent flashing content
 *
 * Example:
 * <AuthGuard>
 *   <ProtectedComponent />
 * </AuthGuard>
 */

import React, { useState, useEffect } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '../../utils/LoadingSpinner';

/**
 * AuthGuard component props
 *
 * @typedef {Object} AuthGuardProps
 * @property {React.ReactNode} children - Protected routes to render
 */
const AuthGuard = ({ children }) => {
  const supabase = useSupabaseClient();
  const location = useLocation();
  const [localSession, setLocalSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initializes session state
   *
   * Fetches session from Supabase and updates local state
   */
  useEffect(() => {
    const initSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setLocalSession(session);
        setError(null);
        setIsLoading(false);
      } catch (error) {
        console.error('Session initialization failed:', error);
        setError('Failed to initialize session');
        setIsLoading(false);
      }
    };

    // Initial session check
    initSession();

    // Auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setLocalSession(session);
      setError(null);
      setIsLoading(false);
    });

    // Storage event listener for cross-tab sync
    const handleStorageChange = event => {
      if (event.key?.startsWith('sb-')) {
        initSession();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [supabase.auth]);

  // Show loading state
  if (isLoading) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  // Show error if authentication check failed
  if (error) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          style={{
            padding: '16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  // Redirect to login if no session and we're done loading
  if (!localSession && !isLoading) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return children;
};

export default AuthGuard;
